import { z } from 'zod';
import { DomainError } from '../errors.js';
import { toIssues } from '../materials/catalog.js';
import { portCodeField } from '../ports/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';

/**
 * Satın alma siparişi (PO; ADR 0026 §1, ADR 0040; `satin-alma-modulu.md` §7;
 * `satin-alma-akisi.jpeg` "Purchase Order"). Seçilen tekliften ofiste açılır, ofiste onaylanır ve
 * yalnız o gemiye iner (mal kabulü için). Hata iletileri koddur; arayüz çevirir.
 */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const optionalDate = () =>
  z.iso
    .date(typed('INVALID_DATE'))
    .nullish()
    .transform((value) => value ?? null);

/**
 * PO durumları (ADR 0040 #176): müşterinin listesi (§7) faturasız. "Approved" tasarımdaki gibi
 * "Issued" adıyla görünür; teslim durumları mal kabulünden türer.
 */
export const PURCHASE_ORDER_STATUSES = [
  'draft',
  'pending_approval',
  'issued',
  'sent',
  'confirmed',
  'partially_delivered',
  'delivered',
  'completed',
  'cancelled',
] as const;
export type PurchaseOrderStatus = (typeof PURCHASE_ORDER_STATUSES)[number];

export interface PurchaseOrderTimes {
  submittedAt: string | null;
  approvedAt: string | null;
  sentAt: string | null;
  confirmedAt: string | null;
  cancelledAt: string | null;
  closedAt: string | null;
}

/** Teslim durumu: hiç, kısmi ya da tam (iptal edilmemiş GRN'lerin kabul edilen miktarı). */
export type DeliveryState = 'none' | 'partial' | 'full';

const PRECISION = 1e6;
const round = (value: number) => Math.round(value * PRECISION) / PRECISION;

/** Kalemlerin sipariş ve kabul edilen miktarından teslim durumu (ADR 0040 #184). */
export function deliveryState(
  lines: readonly { quantity: number; accepted: number }[],
): DeliveryState {
  if (lines.length === 0 || lines.every((line) => round(line.accepted) <= 0)) return 'none';
  return lines.every((line) => round(line.accepted) >= round(line.quantity)) ? 'full' : 'partial';
}

/**
 * PO durumu (ADR 0040 #176): iptal ve kapanış önce gelir; teslim varsa teslim durumu; yoksa
 * teyit, gönderim, onay ve gönderim zamanları. İptal edilmiş PO'ya senkronda gelen kabul durumu
 * değiştirmez (#186); görünüm ayrıca uyarır.
 */
export function purchaseOrderStatus(
  times: PurchaseOrderTimes,
  delivery: DeliveryState,
): PurchaseOrderStatus {
  if (times.cancelledAt) return 'cancelled';
  if (times.closedAt) return 'completed';
  if (delivery === 'full') return 'delivered';
  if (delivery === 'partial') return 'partially_delivered';
  if (times.confirmedAt) return 'confirmed';
  if (times.sentAt) return 'sent';
  if (times.approvedAt) return 'issued';
  return times.submittedAt ? 'pending_approval' : 'draft';
}

/**
 * Sıradaki PO numarası (ADR 0040 #178): şirkette yıl içinde sıralı `PO-YYYY-NNN` (tasarımda
 * "PO-2026-078"). PO yalnız bulutta açılır; tekillik şirket veritabanında.
 */
export function nextPurchaseOrderNo(existing: readonly string[], year: number): string {
  let last = 0;
  for (const no of existing) {
    const match = /^PO-(\d{4})-(\d+)$/.exec(no);
    if (match && Number(match[1]) === year) last = Math.max(last, Number(match[2]));
  }
  return `PO-${year}-${String(last + 1).padStart(3, '0')}`;
}

export interface PurchaseOrderCharges {
  discount: number;
  freight: number;
  customs: number;
  tax: number;
  serviceFee: number;
}

/** Toplam: kalemler − indirim + navlun + gümrük + vergi + hizmet bedeli (`satin-alma-modulu.md` §7). */
export function purchaseOrderTotals(
  lines: readonly { quantity: number; unitPrice: number }[],
  charges: PurchaseOrderCharges,
): { itemsTotal: number; total: number } {
  const itemsTotal = round(lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0));
  const total = round(
    itemsTotal -
      charges.discount +
      charges.freight +
      charges.customs +
      charges.tax +
      charges.serviceFee,
  );
  return { itemsTotal, total };
}

const cents = (value: number) => Math.round(value * 100) / 100;

/**
 * Siparişin üç tutarı (M12c-R4; ADR 0040 #189; D8, ADR 0167 §1): sipariş (`ordered`, kalemler ve
 * masraflarla onaylanan toplam), teslim alınan (`received`) ve kalan (`remaining`), hepsi aynı
 * tabanda. Teslim alınan kalem değeri, indirim ve masraflar (navlun, gümrük, vergi, hizmet) teslim
 * oranında dağıtılarak toplam tabanına çevrilir; açık PO'da Total = Received + Remaining.
 * - İptal edilmiş ve kapanmış (short close) PO'nun kalanı 0'dır: gelmeyecek tutar açık taahhüt
 *   değildir.
 * - Kapanmış PO'nun geçerli toplamı (`total`) teslim alınan tutardır (sahip kararı 2026-11-05):
 *   fatura denetimi ve raporlar bu tutarla yapılır. Sipariş tutarı kayıtta kalır (`ordered`; PO
 *   satırları ve onaylanan içerik hash'i değişmez). Tam teslim edilip kapanmış PO'da ikisi aynıdır.
 * - Öteki durumlarda toplam sipariş tutarıdır.
 */
export function purchaseOrderValues(input: {
  itemsTotal: number;
  total: number;
  receivedItems: number;
  status: PurchaseOrderStatus;
}): { ordered: number; total: number; received: number; remaining: number } {
  const received =
    input.itemsTotal > 0 ? cents((input.receivedItems * input.total) / input.itemsTotal) : 0;
  const finished = input.status === 'cancelled' || input.status === 'completed';
  // Tamamı gelmiş PO'da yuvarlama farkı toplamı değiştirmesin
  const short =
    input.status === 'completed' && round(input.receivedItems) < round(input.itemsTotal);
  return {
    ordered: input.total,
    total: short ? received : input.total,
    received,
    remaining: finished ? 0 : Math.max(0, cents(input.total - received)),
  };
}

export interface PurchaseOrderCurrencyTotal {
  currency: string;
  count: number;
  total: number;
  received: number;
  remaining: number;
}

/**
 * Purchase Order Report özeti (ADR 0040 #189, M12c-R4): para birimine göre (kur varsayımı yok);
 * iptal edilmiş PO tutarlara ve sayıya girmez (yerine açılan PO'yla çift sayılmaz).
 */
export function purchaseOrderSummary(
  rows: readonly {
    currency: string;
    status: PurchaseOrderStatus;
    total: number;
    received: number;
    remaining: number;
  }[],
): PurchaseOrderCurrencyTotal[] {
  const byCurrency = new Map<string, PurchaseOrderCurrencyTotal>();
  for (const row of rows) {
    if (row.status === 'cancelled') continue;
    const entry = byCurrency.get(row.currency) ?? {
      currency: row.currency,
      count: 0,
      total: 0,
      received: 0,
      remaining: 0,
    };
    entry.count += 1;
    entry.total = cents(entry.total + row.total);
    entry.received = cents(entry.received + row.received);
    entry.remaining = cents(entry.remaining + row.remaining);
    byCurrency.set(row.currency, entry);
  }
  return [...byCurrency.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}

/**
 * Taslakta düzeltilen teslim bilgileri (ADR 0040 #179): kalemler ve fiyatlar tekliften gelir,
 * değişmez. Onaya gönderilen PO kilitlidir.
 */
export const purchaseOrderDeliverySchema = z.object({
  /** Beklenen teslim günü (tasarımda "Expected Delivery"). */
  expectedDelivery: optionalDate(),
  deliveryLocation: optionalText(120),
  /** Teslim limanı listede ise kodu (ADR 0048 §3); içerik hash'ine girmez. */
  deliveryPortCode: portCodeField(),
  /** Teslim şartı (ör. "DAP Singapore"). */
  deliveryTerms: optionalText(200),
  paymentTerms: optionalText(200),
  warranty: optionalText(200),
  remarks: optionalText(1000),
});
export type PurchaseOrderDelivery = z.output<typeof purchaseOrderDeliverySchema>;

export function parsePurchaseOrderDelivery(input: unknown): ParseResult<PurchaseOrderDelivery> {
  const result = purchaseOrderDeliverySchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/** Tedarikçinin teyidi (ADR 0040 #182): teyit numarası ve teyit edilen teslim günü. */
export const purchaseOrderConfirmationSchema = z.object({
  supplierRef: optionalText(80),
  confirmedDelivery: optionalDate(),
});
export type PurchaseOrderConfirmation = z.output<typeof purchaseOrderConfirmationSchema>;

export function parsePurchaseOrderConfirmation(
  input: unknown,
): ParseResult<PurchaseOrderConfirmation> {
  const result = purchaseOrderConfirmationSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

export interface PurchaseOrderContentLine {
  lineNo: number;
  materialId: string | null;
  name: string;
  description: string | null;
  quantity: number;
  unit: string;
  unitPrice: number;
  /** İkame satırı (ADR 0111 §D): istenen kalemin yerine başka ürün; işaret hash'e girer. */
  substitute?: boolean;
  substituteFor?: string | null;
}

export interface PurchaseOrderContentInput {
  poNo: string;
  vesselId: string;
  requisitionId: string;
  rfqId: string;
  quotationId: string;
  supplierId: string;
  supplierName: string;
  currency: string;
  paymentTerms: string | null;
  deliveryTerms: string | null;
  deliveryLocation: string | null;
  warranty: string | null;
  charges: PurchaseOrderCharges;
  lines: readonly PurchaseOrderContentLine[];
}

/**
 * Onaylanan içerik (ADR 0040 #177; mimari belge §7.3): hash'in girdisi. Ticari alanlardır:
 * tedarikçi, para birimi, şartlar, kalemler ve toplam. Beklenen teslim günü ve not girmez:
 * tedarikçinin teyidiyle değişebilir, yeniden onay istemez.
 */
export function purchaseOrderContent(input: PurchaseOrderContentInput) {
  const lines = [...input.lines]
    .sort((a, b) => a.lineNo - b.lineNo)
    .map((line) => ({
      lineNo: line.lineNo,
      materialId: line.materialId,
      name: line.name,
      description: line.description,
      quantity: line.quantity,
      unit: line.unit,
      unitPrice: line.unitPrice,
      // Yalnız ikame satırında: eski siparişlerin hash'i değişmez
      ...(line.substitute ? { substitute: true, substituteFor: line.substituteFor ?? null } : {}),
    }));
  return {
    poNo: input.poNo,
    vesselId: input.vesselId,
    requisitionId: input.requisitionId,
    rfqId: input.rfqId,
    quotationId: input.quotationId,
    supplierId: input.supplierId,
    supplierName: input.supplierName,
    currency: input.currency,
    paymentTerms: input.paymentTerms,
    deliveryTerms: input.deliveryTerms,
    deliveryLocation: input.deliveryLocation,
    warranty: input.warranty,
    charges: { ...input.charges },
    lines,
    total: purchaseOrderTotals(lines, input.charges).total,
  };
}
export type PurchaseOrderContent = ReturnType<typeof purchaseOrderContent>;

export type PurchaseOrderAction =
  | 'edit'
  | 'submit'
  | 'decide'
  | 'send'
  | 'markSent'
  | 'confirm'
  | 'receive'
  | 'cancel'
  | 'close'
  | 'cancelReceipt';

const FINISHED: ReadonlySet<PurchaseOrderStatus> = new Set(['completed', 'cancelled']);
const ISSUED_AND_OPEN: ReadonlySet<PurchaseOrderStatus> = new Set([
  'issued',
  'sent',
  'confirmed',
  'partially_delivered',
]);

/**
 * PO eylemi bu durumda yapılabilir mi (ADR 0040 #176–#182, #186); değilse kodla reddeder.
 * - Düzeltme ve onaya gönderme yalnız taslakta (`PO_LOCKED`); karar onay beklerken.
 * - Gönderim onaylı ve teslim tamamlanmamış PO'da (yeniden gönderim olur).
 * - Mal kabulü onaylı, iptal edilmemiş, kapanmamış ve tamamı gelmemiş PO'ya.
 * - İptal kabul girilmemiş PO'da (`PO_HAS_RECEIPTS`); kapanış `closeRequirement` kuralıyla.
 */
export function assertPurchaseOrderAction(
  status: PurchaseOrderStatus,
  action: PurchaseOrderAction,
  context: { hasReceipts?: boolean; reason?: string | null },
): void {
  const fail = (code: string): never => {
    throw new DomainError(code, `${action}: ${status}`);
  };
  switch (action) {
    case 'edit':
    case 'submit':
      if (status !== 'draft') fail('PO_LOCKED');
      return;
    case 'decide':
      if (status !== 'pending_approval') fail('NOT_PENDING');
      return;
    case 'send':
      if (status === 'draft' || status === 'pending_approval') fail('PO_NOT_ISSUED');
      if (!ISSUED_AND_OPEN.has(status)) fail('PO_CLOSED');
      return;
    case 'markSent':
      if (status === 'draft' || status === 'pending_approval') fail('PO_NOT_ISSUED');
      if (status !== 'issued') fail('PO_ALREADY_SENT');
      return;
    case 'confirm':
      if (status === 'draft' || status === 'pending_approval') fail('PO_NOT_ISSUED');
      if (status !== 'issued' && status !== 'sent') fail('PO_ALREADY_CONFIRMED');
      return;
    case 'receive':
      if (!ISSUED_AND_OPEN.has(status)) fail('PO_NOT_RECEIVABLE');
      return;
    case 'cancel':
      if (FINISHED.has(status)) fail('PO_CLOSED');
      if (context.hasReceipts) fail('PO_HAS_RECEIPTS');
      return;
    case 'cancelReceipt':
      // Kapanmış PO'nun GRN'si iptal edilmez (M12c-R2): düzeltme GRN'si girilemez, stok defteri
      // fiziksel stoktan ayrılır ve kapanış gerekçe kuralı dolaylı aşılır. İptal edilmiş PO'ya
      // senkronla gelen GRN (#186) iptal edilebilir.
      if (status === 'completed') fail('PO_CLOSED');
      return;
    case 'close': {
      const requirement = closeRequirement(status, context.hasReceipts ?? false);
      if (requirement === null) fail('PO_NOT_CLOSABLE');
      if (requirement === 'required' && !context.reason?.trim()) fail('CLOSE_REASON_REQUIRED');
      return;
    }
  }
}

/**
 * Kapanış ("Completed") gerekçe ister mi; kapatılamıyorsa `null` (ADR 0040 #182, M12c-R1).
 * - Teslim edilmiş PO gerekçesiz kapanır (gerekçe isteğe bağlı).
 * - Kısmi teslimde kalan gelmeyecekse gerekçeyle (short close).
 * - Onaylı, açık ve iptal edilmemiş GRN'si olan ama kabulü sıfır PO (ör. tümü hasarlı ya da
 *   reddedilen teslimat, tedarikçi yeniden göndermiyor) da gerekçeyle kapanır: GRN tedarikçiye
 *   karşı delil olarak kalır; PO iptal edilmez (`PO_HAS_RECEIPTS`).
 * - GRN'si olmayan açık PO kapanmaz; gerekçeyle iptal edilir.
 */
export function closeRequirement(
  status: PurchaseOrderStatus,
  hasReceipts: boolean,
): 'optional' | 'required' | null {
  if (status === 'delivered') return 'optional';
  if (status === 'partially_delivered') return 'required';
  if (hasReceipts && (status === 'issued' || status === 'sent' || status === 'confirmed')) {
    return 'required';
  }
  return null;
}
